import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertCircle,
  Calendar,
  CheckCircle2,
  Clock,
  Heart,
  Loader2,
  MessageCircle,
  Phone,
  Pill,
  Send,
  ShoppingCart,
  Star,
  UserCheck,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { getApiErrorMessage } from '../../lib/apiError';
import {
  formatWatDateTime,
  getVisitDependantName,
  getVisitStart,
  normalizeVisitStatus,
  sortVisitsNewestFirst,
} from '../../lib/visitUtils';

const firstNameOf = (item, fallback = 'there') => (
  item?.firstName || item?.first_name || item?.name?.split(' ')?.[0] || fallback
);

const fullNameOf = (item, fallback = 'Magnolia user') => {
  if (!item) return fallback;
  return item.name || [item.firstName || item.first_name, item.lastName || item.last_name].filter(Boolean).join(' ') || fallback;
};

const medicationName = (item) => item?.name || item?.medicationName || item?.medication_name || 'Medication';
const medicationTime = (item) => item?.time || item?.scheduledTime || item?.scheduled_time || '';

const WellnessCircle = ({ score }) => {
  const numericScore = Number(score);
  const hasScore = Number.isFinite(numericScore);
  const safeScore = hasScore ? Math.max(0, Math.min(100, numericScore)) : 0;
  const color = safeScore >= 75 ? '#2F7D4C' : safeScore >= 50 ? '#C9A961' : '#A33232';
  const radius = 58;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (safeScore / 100) * circumference;
  return (
    <div className="flex items-center gap-5">
      <div className="relative h-36 w-36 flex-shrink-0">
        <svg width="144" height="144" viewBox="0 0 144 144">
          <circle cx="72" cy="72" r={radius} fill="none" stroke="#E8E6E3" strokeWidth="10" />
          <circle
            cx="72"
            cy="72"
            r={radius}
            fill="none"
            stroke={color}
            strokeLinecap="round"
            strokeWidth="10"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            transform="rotate(-90 72 72)"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-serif text-4xl font-semibold" style={{ color }}>{hasScore ? safeScore : '-'}</span>
          <span className="text-xs font-medium uppercase tracking-[0.12em] text-[#5A5A5A]">Wellness</span>
        </div>
      </div>
      <div>
        <p className="caption text-[#9F1D35]">Today</p>
        <h2 className="mt-2 font-serif text-2xl font-semibold text-[#1A1A1A]">
          {!hasScore ? 'Your first wellness score will appear after a visit.' : safeScore >= 75 ? "You're doing well." : safeScore >= 50 ? "We're keeping an eye on things." : "Your care team is watching closely."}
        </h2>
        <p className="mt-2 text-sm leading-6 text-[#5A5A5A]">This score comes from completed visit checklist ratings.</p>
      </div>
    </div>
  );
};

const VisitCard = ({ visit, onOpen }) => {
  const status = normalizeVisitStatus(visit.status);
  const isApproved = status === 'Approved';
  return (
    <button
      type="button"
      onClick={() => onOpen(visit)}
      className="w-full rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-4 text-left transition-colors hover:border-[#D8D5D0]"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="flex items-center gap-2 text-sm font-medium text-[#1A1A1A]">
            <Calendar className="h-4 w-4 text-[#9F1D35]" />
            {formatWatDateTime(getVisitStart(visit))}
          </p>
          <p className="mt-1 text-sm text-[#5A5A5A]">{visit.careOfficerName || visit.careOfficerSnapshot?.name || 'Care officer'}</p>
        </div>
        <span className={`w-fit rounded-full border px-3 py-1 text-xs font-medium ${
          isApproved ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-[#E8E6E3] bg-[#F7F4EE] text-[#5A5A5A]'
        }`}>
          {isApproved ? 'Approved report' : status}
        </span>
      </div>
      {isApproved && (
        <p className="mt-3 line-clamp-2 text-sm leading-6 text-[#5A5A5A]">
          {visit.generalComments || visit.checklist?.generalComments || visit.checklist?.otherObservations || 'Visit report is ready to view.'}
        </p>
      )}
    </button>
  );
};

const DependantDashboard = () => {
  const navigate = useNavigate();
  const {
    currentUser,
    dependants,
    visits,
    careOfficers,
    medicationLogs,
    startCareOfficerConversation,
    startFamilyConversation,
    createSupplyRequest,
    triggerEmergency,
    refreshEmergency,
    submitEmergencyFeedback,
    markMedicationTaken,
  } = useApp();
  const [showSupplyRequest, setShowSupplyRequest] = useState(false);
  const [supplyCategory, setSupplyCategory] = useState('Groceries');
  const [supplyDescription, setSupplyDescription] = useState('');
  const [supplyUrgent, setSupplyUrgent] = useState(false);
  const [submittingSupply, setSubmittingSupply] = useState(false);
  const [emergencyActive, setEmergencyActive] = useState(false);
  const [emergencyStage, setEmergencyStage] = useState('idle'); // 'sending' | 'sent' | 'error'
  const [emergencyRecord, setEmergencyRecord] = useState(null);
  const [emergencyLocationNote, setEmergencyLocationNote] = useState('');
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackHover, setFeedbackHover] = useState(0);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackError, setFeedbackError] = useState(null);
  const [feedbackDone, setFeedbackDone] = useState(false);

  const dependant = dependants[0] || currentUser || {};
  const dependantId = dependant.id || dependant._id || currentUser?.profileId;
  const dependantVisits = useMemo(() => (
    visits.filter((visit) => !dependantId || visit.dependantId === dependantId || visit.dependant_id === dependantId)
  ), [dependantId, visits]);
  const upcomingVisits = useMemo(() => (
    dependantVisits
      .filter((visit) => normalizeVisitStatus(visit.status) === 'Scheduled')
      .sort((a, b) => new Date(getVisitStart(a) || 0) - new Date(getVisitStart(b) || 0))
  ), [dependantVisits]);
  const approvedVisits = useMemo(() => (
    sortVisitsNewestFirst(dependantVisits.filter((visit) => normalizeVisitStatus(visit.status) === 'Approved'))
  ), [dependantVisits]);
  const primaryOfficerId = upcomingVisits[0]?.careOfficerId || approvedVisits[0]?.careOfficerId || dependant.assignedCareOfficer || dependant.assigned_care_officer;
  const careOfficer = careOfficers.find((officer) => officer.id === primaryOfficerId || officer._id === primaryOfficerId);
  const medications = Array.isArray(dependant.medications) ? dependant.medications : [];
  const greetingHour = new Date().getHours();
  const greeting = greetingHour < 12 ? 'Good morning' : greetingHour < 17 ? 'Good afternoon' : 'Good evening';

  const isMedicationTaken = (medication) => (
    medicationLogs.some((log) => (
      (log.medicationName || log.medication_name) === medicationName(medication)
      && (log.scheduledTime || log.scheduled_time || '') === medicationTime(medication)
    ))
  );

  const openCareOfficerChat = async () => {
    if (!primaryOfficerId) {
      toast.error('No care officer is linked yet.');
      return;
    }
    try {
      const conversation = await startCareOfficerConversation(primaryOfficerId);
      navigate(`/messages?conversation=${conversation.id}`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to open care officer chat.'));
    }
  };

  const openFamilyChat = async () => {
    try {
      const conversation = await startFamilyConversation();
      navigate(`/messages?conversation=${conversation.id}`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to open family chat.'));
    }
  };

  const getDeviceLocation = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Location services are not available on this device.'));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 8000,
      maximumAge: 0,
    });
  });

  const handleEmergency = async () => {
    setEmergencyActive(true);
    setEmergencyStage('sending');
    setEmergencyRecord(null);
    setEmergencyLocationNote('');
    setFeedbackRating(0);
    setFeedbackHover(0);
    setFeedbackComment('');
    setFeedbackError(null);
    setFeedbackDone(false);

    let location = {};
    try {
      const position = await getDeviceLocation();
      location = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy_m: position.coords.accuracy,
      };
    } catch (geoError) {
      setEmergencyLocationNote('Could not get your GPS location. Sending the alert without it.');
    }

    try {
      const record = await triggerEmergency(location);
      setEmergencyRecord(record);
      setEmergencyStage('sent');
      if (record.hotlineNumber) {
        window.location.href = `tel:${record.hotlineNumber}`;
      }
    } catch (error) {
      setEmergencyStage('error');
      toast.error(getApiErrorMessage(error, 'Unable to trigger emergency alert.'));
    }
  };

  const closeEmergencyModal = () => {
    setEmergencyActive(false);
    setEmergencyStage('idle');
    setEmergencyRecord(null);
    setEmergencyLocationNote('');
    setFeedbackRating(0);
    setFeedbackHover(0);
    setFeedbackComment('');
    setFeedbackError(null);
    setFeedbackDone(false);
  };

  const handleSubmitFeedback = async () => {
    if (!feedbackRating || !emergencyRecord?.id) return;
    setFeedbackSubmitting(true);
    setFeedbackError(null);
    try {
      await submitEmergencyFeedback(emergencyRecord.id, feedbackRating, feedbackComment.trim());
      const refreshed = await refreshEmergency(emergencyRecord.id);
      setEmergencyRecord(refreshed);
      setFeedbackDone(true);
      toast.success('Thanks for your feedback.');
    } catch (error) {
      setFeedbackError(getApiErrorMessage(error, 'Could not submit feedback. Please try again.'));
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const ratingLabels = ['', 'Poor', 'Fair', 'Good', 'Very good', 'Excellent'];

  useEffect(() => {
    if (!emergencyActive || emergencyStage !== 'sent' || !emergencyRecord?.id) return undefined;
    const interval = setInterval(async () => {
      try {
        const updated = await refreshEmergency(emergencyRecord.id);
        setEmergencyRecord(updated);
      } catch (pollError) {
        // Transient polling failure - the next interval tick will retry.
      }
    }, 6000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emergencyActive, emergencyStage, emergencyRecord?.id]);

  const submitSupplyRequest = async (event) => {
    event.preventDefault();
    if (!supplyDescription.trim()) {
      toast.error('Describe what you need first.');
      return;
    }
    setSubmittingSupply(true);
    try {
      await createSupplyRequest({ category: supplyCategory, description: supplyDescription.trim(), urgent: supplyUrgent });
      toast.success('Supply request sent.');
      setShowSupplyRequest(false);
      setSupplyDescription('');
      setSupplyUrgent(false);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to send supply request.'));
    } finally {
      setSubmittingSupply(false);
    }
  };

  const handleMedicationTaken = async (medication) => {
    try {
      await markMedicationTaken({ medicationName: medicationName(medication), scheduledTime: medicationTime(medication) });
      toast.success('Medication marked as taken.');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to save medication log.'));
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F4EE]" data-testid="dependant-dashboard">
      {showSupplyRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#1A1A1A]/50 px-4">
          <form onSubmit={submitSupplyRequest} className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-6">
            <h2 className="font-serif text-2xl font-semibold text-[#1A1A1A]">Request supplies</h2>
            <p className="mt-1 text-sm text-[#5A5A5A]">Tell Magnolia what you need and your care team will follow up.</p>
            <label className="mt-5 block text-sm font-medium text-[#1A1A1A]">
              Category
              <select
                value={supplyCategory}
                onChange={(event) => setSupplyCategory(event.target.value)}
                className="mt-2 h-11 w-full rounded-full border border-[#D8D5D0] bg-[#FAFAF7] px-4"
              >
                {['Groceries', 'Medication', 'Water', 'Power', 'Food', 'Other'].map((item) => <option key={item} value={item}>{item}</option>)}
              </select>
            </label>
            <label className="mt-4 block text-sm font-medium text-[#1A1A1A]">
              What do you need?
              <textarea
                value={supplyDescription}
                onChange={(event) => setSupplyDescription(event.target.value)}
                rows={4}
                className="mt-2 w-full rounded-2xl border border-[#D8D5D0] bg-[#FAFAF7] p-3 text-sm outline-none focus:border-[#9F1D35]"
                placeholder="e.g. Rice, blood pressure medicine, drinking water..."
              />
            </label>
            <label className="mt-4 flex items-center gap-3 text-sm text-[#5A5A5A]">
              <input type="checkbox" checked={supplyUrgent} onChange={(event) => setSupplyUrgent(event.target.checked)} />
              This is urgent
            </label>
            <div className="mt-6 flex gap-3">
              <button type="button" onClick={() => setShowSupplyRequest(false)} className="flex-1 rounded-full border border-[#9F1D35] px-4 py-3 text-sm font-medium text-[#9F1D35]">Cancel</button>
              <button type="submit" disabled={submittingSupply} className="flex-1 rounded-full bg-[#9F1D35] px-4 py-3 text-sm font-medium text-[#FAFAF7] disabled:opacity-60">
                {submittingSupply ? 'Sending...' : 'Send'}
              </button>
            </div>
          </form>
        </div>
      )}

      {emergencyActive && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#7A1628] px-4 py-8 text-[#FAFAF7]">
          <div className="max-h-[90vh] w-full max-w-md overflow-y-auto text-center">
            {emergencyStage === 'sending' && (
              <>
                <Loader2 className="mx-auto mb-5 h-16 w-16 animate-spin" />
                <h2 className="font-serif text-3xl font-semibold">Contacting MALA'IKA...</h2>
                <p className="mt-3 text-[#F5E8EA]">Getting your location and opening an emergency case. This takes a few seconds.</p>
              </>
            )}

            {emergencyStage === 'error' && (
              <>
                <AlertCircle className="mx-auto mb-5 h-16 w-16" />
                <h2 className="font-serif text-3xl font-semibold">Couldn't send the alert</h2>
                <p className="mt-3 text-[#F5E8EA]">Please try again, or call your care officer directly if this keeps happening.</p>
                <div className="mt-6 flex justify-center gap-3">
                  <button type="button" onClick={closeEmergencyModal} className="rounded-full border border-[#FAFAF7] px-6 py-3 text-sm font-medium text-[#FAFAF7]">
                    Close
                  </button>
                  <button type="button" onClick={handleEmergency} className="rounded-full bg-[#FAFAF7] px-6 py-3 text-sm font-medium text-[#9F1D35]">
                    Try again
                  </button>
                </div>
              </>
            )}

            {emergencyStage === 'sent' && emergencyRecord && (
              <>
                <AlertCircle className="mx-auto mb-4 h-14 w-14" />
                <h2 className="font-serif text-3xl font-semibold">Emergency alert sent</h2>
                <p className="mt-2 text-[#F5E8EA]">
                  Status: <span className="font-semibold">{emergencyRecord.status || 'Triggered'}</span>
                </p>

                {emergencyLocationNote && (
                  <p className="mt-3 rounded-xl bg-[#9F1D35]/40 px-4 py-2 text-sm text-[#F5E8EA]">{emergencyLocationNote}</p>
                )}
                {emergencyRecord.malaikaWarning && (
                  <p className="mt-3 rounded-xl bg-[#9F1D35]/40 px-4 py-2 text-sm text-[#F5E8EA]">{emergencyRecord.malaikaWarning}</p>
                )}

                {emergencyRecord.malaika?.correlationCode && (
                  <p className="mt-3 text-sm text-[#F5E8EA]">
                    Reference code for MALA'IKA: <span className="font-semibold">{emergencyRecord.malaika.correlationCode}</span>
                  </p>
                )}

                {emergencyRecord.hotlineNumber && (
                  <a
                    href={`tel:${emergencyRecord.hotlineNumber}`}
                    className="mt-5 flex items-center justify-center gap-2 rounded-full bg-[#FAFAF7] px-6 py-3 text-sm font-medium text-[#9F1D35]"
                  >
                    <Phone className="h-4 w-4" /> Call MALA'IKA hotline {emergencyRecord.hotlineNumber}
                  </a>
                )}

                {Array.isArray(emergencyRecord.timeline) && emergencyRecord.timeline.length > 0 && (
                  <div className="mt-5 space-y-2 rounded-2xl bg-[#FAFAF7]/10 p-4 text-left">
                    {emergencyRecord.timeline.slice().reverse().map((entry, index) => (
                      <div key={`${entry.timestamp}-${index}`} className="text-sm text-[#F5E8EA]">
                        <span className="font-medium">{entry.action}</span>
                        {entry.actor ? ` — ${entry.actor}` : ''}
                      </div>
                    ))}
                  </div>
                )}

                {(emergencyRecord.status === 'Resolved' || emergencyRecord.status === 'False Alarm') && emergencyRecord.malaika?.feedbackEligible && (
                  (feedbackDone || emergencyRecord.malaika?.feedbackSubmitted) ? (
                    <div className="mt-6 rounded-2xl bg-[#FAFAF7]/10 p-5" data-testid="emergency-feedback-thanks">
                      <CheckCircle2 className="mx-auto mb-2 h-8 w-8 text-[#FAFAF7]" />
                      <p className="text-sm text-[#F5E8EA]">Thank you. Your feedback helps MALA'IKA improve emergency response.</p>
                    </div>
                  ) : (
                    <div className="mt-6 rounded-2xl bg-[#FAFAF7]/10 p-5 text-left" data-testid="emergency-feedback-form">
                      <p className="text-center text-sm font-medium text-[#FAFAF7]">How was MALA'IKA's response?</p>
                      <div className="mt-3 flex justify-center gap-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => setFeedbackRating(star)}
                            onMouseEnter={() => setFeedbackHover(star)}
                            onMouseLeave={() => setFeedbackHover(0)}
                            className="p-1 transition-transform hover:scale-110"
                            aria-label={`Rate ${star} of 5`}
                            data-testid={`emergency-feedback-star-${star}`}
                          >
                            <Star
                              className="h-8 w-8 transition-colors"
                              fill={(feedbackHover || feedbackRating) >= star ? '#F5C451' : 'transparent'}
                              stroke={(feedbackHover || feedbackRating) >= star ? '#F5C451' : '#F5E8EA'}
                              strokeWidth={1.5}
                            />
                          </button>
                        ))}
                      </div>
                      {feedbackRating > 0 && (
                        <p className="mt-2 text-center text-sm font-medium text-[#F5C451]">{ratingLabels[feedbackRating]}</p>
                      )}
                      <textarea
                        value={feedbackComment}
                        onChange={(event) => setFeedbackComment(event.target.value)}
                        rows={3}
                        maxLength={1000}
                        placeholder="Add a comment (optional)"
                        className="mt-3 w-full rounded-xl border border-[#FAFAF7]/30 bg-transparent p-3 text-sm text-[#FAFAF7] outline-none placeholder:text-[#F5E8EA]/60 focus:border-[#FAFAF7]"
                        data-testid="emergency-feedback-comment"
                      />
                      {feedbackError && <p className="mt-2 text-sm text-[#FBD3D9]">{feedbackError}</p>}
                      <button
                        type="button"
                        onClick={handleSubmitFeedback}
                        disabled={!feedbackRating || feedbackSubmitting}
                        className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-[#FAFAF7] px-6 py-3 text-sm font-medium text-[#9F1D35] disabled:opacity-60"
                        data-testid="emergency-feedback-submit"
                      >
                        {feedbackSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        {feedbackSubmitting ? 'Submitting...' : 'Submit feedback'}
                      </button>
                    </div>
                  )
                )}

                <button type="button" onClick={closeEmergencyModal} className="mt-6 rounded-full border border-[#FAFAF7] px-6 py-3 text-sm font-medium text-[#FAFAF7]">
                  Close
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:py-8">
        <section className="grid gap-6 lg:grid-cols-[1fr_360px]">
          <div className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-6">
            <p className="caption text-[#9F1D35]">{greeting}</p>
            <h1 className="mt-3 font-serif text-3xl font-semibold text-[#1A1A1A] md:text-4xl">
              {firstNameOf(dependant)}, your care team is here.
            </h1>
            <p className="mt-3 max-w-2xl text-base leading-7 text-[#5A5A5A]">
              You can see your visits, contact your care officer, request supplies, or send an emergency alert from this page.
            </p>
            <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <button onClick={() => careOfficer?.phone ? window.location.href = `tel:${careOfficer.phone}` : toast.error('No care officer phone available.')} className="rounded-2xl bg-[#9F1D35] px-4 py-5 text-left text-[#FAFAF7]">
                <Phone className="mb-3 h-6 w-6" />
                <span className="font-medium">Call care officer</span>
              </button>
              <button onClick={openCareOfficerChat} className="rounded-2xl border border-[#9F1D35] px-4 py-5 text-left text-[#9F1D35]">
                <MessageCircle className="mb-3 h-6 w-6" />
                <span className="font-medium">Message care officer</span>
              </button>
              <button onClick={() => setShowSupplyRequest(true)} className="rounded-2xl border border-[#E8E6E3] bg-[#F7F4EE] px-4 py-5 text-left text-[#1A1A1A]">
                <ShoppingCart className="mb-3 h-6 w-6 text-[#9F1D35]" />
                <span className="font-medium">Need supplies</span>
              </button>
              <button onClick={handleEmergency} className="rounded-2xl bg-[#A33232] px-4 py-5 text-left text-[#FAFAF7]">
                <AlertCircle className="mb-3 h-6 w-6" />
                <span className="font-medium">Emergency SOS</span>
              </button>
            </div>
          </div>

          <div className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-6">
            <WellnessCircle score={dependant.overallWellnessScore ?? dependant.overall_wellness_score} />
          </div>
        </section>

        <section className="mt-6 grid gap-6 lg:grid-cols-[1fr_360px]">
          <div className="space-y-6">
            <section className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-5">
              <div className="mb-4 flex items-center justify-between gap-3">
                <div>
                  <h2 className="font-serif text-2xl font-semibold text-[#1A1A1A]">Visits</h2>
                  <p className="text-sm text-[#5A5A5A]">Upcoming appointments and approved reports.</p>
                </div>
                <button type="button" onClick={() => navigate('/visits')} className="rounded-full border border-[#9F1D35] px-4 py-2 text-sm font-medium text-[#9F1D35]">
                  View all
                </button>
              </div>
              <div className="space-y-3">
                {[...upcomingVisits.slice(0, 2), ...approvedVisits.slice(0, 3)].map((visit) => (
                  <VisitCard key={visit.id} visit={visit} onOpen={(item) => navigate(normalizeVisitStatus(item.status) === 'Approved' ? `/visits/${item.id}` : '/visits')} />
                ))}
                {upcomingVisits.length === 0 && approvedVisits.length === 0 && (
                  <div className="rounded-2xl border border-dashed border-[#D8D5D0] p-8 text-center">
                    <Clock className="mx-auto mb-3 h-10 w-10 text-[#D8D5D0]" />
                    <p className="font-medium text-[#1A1A1A]">No visits to show yet</p>
                    <p className="mt-1 text-sm text-[#5A5A5A]">Approved reports and scheduled visits will appear here.</p>
                  </div>
                )}
              </div>
            </section>

            <section className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-5">
              <h2 className="font-serif text-2xl font-semibold text-[#1A1A1A]">Messages</h2>
              <p className="mt-1 text-sm text-[#5A5A5A]">Reach your family or care officer inside Magnolia.</p>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <button type="button" onClick={openFamilyChat} className="flex items-center gap-3 rounded-2xl border border-[#E8E6E3] bg-[#F7F4EE] p-4 text-left">
                  <Heart className="h-5 w-5 text-[#A33232]" />
                  <span className="font-medium text-[#1A1A1A]">Message family</span>
                </button>
                <button type="button" onClick={openCareOfficerChat} className="flex items-center gap-3 rounded-2xl border border-[#E8E6E3] bg-[#F7F4EE] p-4 text-left">
                  <UserCheck className="h-5 w-5 text-[#9F1D35]" />
                  <span className="font-medium text-[#1A1A1A]">Message care officer</span>
                </button>
              </div>
            </section>
          </div>

          <aside className="space-y-6">
            <section className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-5">
              <h2 className="mb-4 flex items-center gap-2 font-serif text-xl font-semibold text-[#1A1A1A]">
                <UserCheck className="h-5 w-5 text-[#9F1D35]" />
                Care officer
              </h2>
              {careOfficer ? (
                <div>
                  <p className="font-medium text-[#1A1A1A]">{fullNameOf(careOfficer, 'Care officer')}</p>
                  <p className="mt-1 text-sm text-[#5A5A5A]">{careOfficer.phone || 'Phone not available yet'}</p>
                  <p className="mt-3 inline-flex items-center gap-1 rounded-full bg-[#F5E8EA] px-3 py-1 text-sm font-medium text-[#9F1D35]">
                    <Star className="h-4 w-4 fill-[#C9A961] text-[#C9A961]" />
                    {careOfficer.rating || '-'} rating
                  </p>
                </div>
              ) : (
                <p className="text-sm text-[#5A5A5A]">A care officer will appear here after scheduling.</p>
              )}
            </section>

            <section className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-5">
              <h2 className="mb-4 flex items-center gap-2 font-serif text-xl font-semibold text-[#1A1A1A]">
                <Pill className="h-5 w-5 text-[#9F1D35]" />
                Today's medications
              </h2>
              <div className="space-y-3">
                {medications.map((medication, index) => {
                  const taken = isMedicationTaken(medication);
                  return (
                    <div key={`${medicationName(medication)}-${index}`} className="rounded-2xl border border-[#E8E6E3] bg-[#F7F4EE] p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-medium text-[#1A1A1A]">{medicationName(medication)}</p>
                          <p className="text-sm text-[#5A5A5A]">{medicationTime(medication) || 'As directed'}</p>
                        </div>
                        {taken ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            Taken
                          </span>
                        ) : (
                          <button type="button" onClick={() => handleMedicationTaken(medication)} className="rounded-full bg-[#9F1D35] px-3 py-1.5 text-xs font-medium text-[#FAFAF7]">
                            Mark taken
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
                {medications.length === 0 && <p className="text-sm text-[#5A5A5A]">No medications were registered.</p>}
              </div>
            </section>
          </aside>
        </section>
      </main>
    </div>
  );
};

export default DependantDashboard;
